import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, map, Observable, tap, withLatestFrom } from 'rxjs';
import { DateUtilityService, toLocalDateTimeString } from '../../../../../shared-core/services/date-utility.service';
import { InputMode } from '../../../core/editable-fields/text-input/text-input.component';
import { DisplayDetailsModalComponent } from '../../../core/modals/display-details/display-details.component';
import { LogCategory, LoggerService, LogPage, LogType } from '../../../core/services/logger.service';
import { ProfileService } from '../../../core/services/profile.service';
import { FacilitiesEventService } from '../../../services/facilities.event.services';
import { PeopleService } from '../../../services/people.service';

@Component({
    selector: 'app-facilities-events',
    templateUrl: './events.component.html',
    styleUrls: ['./events.component.scss'],
    standalone: false
})
export class FacilitiesEventsComponent implements OnInit {
    public peopleService = inject(PeopleService);
    private profileService = inject(ProfileService);
    private appService = inject(FacilitiesEventService);
    private logger = inject(LoggerService);
    private router = inject(Router);
    private modalService = inject(NgbModal);

    public now: Date = new Date();
    public isWaiting: boolean = false;

    //  Different labelcol sizes for desktop and mobile screens
    public labelColsSm: number = 12;
    public labelColsMd: number = 3;
    public labelColsLg: number = 3;

    public newEvent: any = {
        additionalContact: null,
        additionalContactEmail: '',
        title: '',
        description: '',
        location: '',
        inBell: false,
        inCharlotte: false,
        inKenan: false,
        inKoury: false,
        inMcColl: false,
        startDate: DateUtilityService.nullDateString,
        endDate: DateUtilityService.nullDateString,
        includesHelp: false,
        includesCatering: false,
        cateringDescription: '',
        includesAv: false,
        isRental: false,
        includesAventri: false,
        includesHousekeeping: false,
        includesTent: false,
        tentDescription: '',
        includesFurniture: false,
        tableCount: 0,
        chairCount: 0,
        includesTrash: false,
        trashCanCount: 0,
        hasErrors: false,
        errors$: new BehaviorSubject({}),
        warnings$: new BehaviorSubject({}),
    }

    public context$: Observable<any>;

    public get inputMode(): typeof InputMode {
        return InputMode;
    }

    constructor() {
        this.log("constructor", null, LogCategory.Constructor);}

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Page, LogPage.Facilities, category);
    }

    public ngOnInit() {
        this.log("ngOnInit", null, LogCategory.Init);

        this.context$ = this.appService.settings$().pipe(
            withLatestFrom(this.profileService.profile$),
            map(([settings, profile]) => {
                return { person: profile.biodata, settings: settings };
            }),
            tap((context) => {
                this.newEvent.onChange = () => { this.newEvent.IsDirty = true };
                this.newEvent.validate = () => this.validate(this.newEvent, context.settings);
            })
        )
    }

    public submit() {
        this.log("onSubmit", "Submitting form to controller.");
        if (!this.newEvent.hasErrors) {

            //  Transform the outbound object so it doesn't interfere with the form and doesn't
            //  include observables in the submission object.
            const fieldsToCopy = ['additionalContactEmail', 'title', 'description', 'location',
                'inBell', 'inCharlotte', 'inKenan', 'inKoury', 'inMcColl', 'startDate', 'endDate',
                'includesHelp', 'includesCatering', 'cateringDescription', 'includesAv', 'isRental',
                'includesAventri', 'includesHousekeeping', 'includesTent', 'tentDescription',
                'includesFurniture', 'tableCount', 'chairCount', 'includesTrash', 'trashCanCount'];

            let submitEvent: any = {};

            for (let key of Object.keys(this.newEvent)) {
                if (fieldsToCopy.includes(key))
                    submitEvent[key] = this.newEvent[key];
            }


            submitEvent.startDate = toLocalDateTimeString(submitEvent.startDate);
            submitEvent.endDate = toLocalDateTimeString(submitEvent.endDate);

            this.isWaiting = true;
            this.appService.eventForm$.next(submitEvent);
        }
    }

    public response$ = this.appService.submit$.pipe(
        tap((res) => {
            this.isWaiting = false;

            const modalRef = this.modalService.open(DisplayDetailsModalComponent);
            let title = "Event Request Created";
            let body = "Thank you for submitting an event request.  You will receive an email confirmation shortly.";

            if (res instanceof HttpErrorResponse) {
                title = "Error Creating Request";
                body = "There was an error creating your request. Please contact <a href='mailto:AppDevSupport@kenan-flagler.unc.edu'>AppDevSupport@kenan-flagler.unc.edu</a>.";
            }

            modalRef.componentInstance.title = title;
            modalRef.componentInstance.body = body;
            modalRef.componentInstance.okButton = "Close";
            //  If they click the close button or the dismiss X re-route to the main
            //  facilities page if this wasn't a failure.
            modalRef.result.finally(() => {
                if (!(res instanceof HttpErrorResponse))
                    this.close();

            });
        })
    )

    public close() {
        this.router.navigate(['/facilities'])
    }

    //  Resets the value to 0 if it's not a number.  Prevents NaN issues.
    public testNumber(value: any, prop: string) {
        this.newEvent[prop] = Number(value) || 0;
        this.newEvent.validate();
    }

    public validate(obj: any, settings: any) {
        obj.errors = {};
        obj.warnings = {};
        obj.hasErrors = false;

        if (!!settings) {
            obj.warnings['includesAv'] = settings.AudioVisualAdvanceNotice;
            obj.warnings['includesAventri'] = settings.AventriAdvanceNotice;
            obj.warnings['includesCatering'] = settings.CateringAdvanceNotice;
            obj.warnings['includesHousekeeping'] = settings.HouseKeepingAdvanceNotice;
            obj.warnings['includesFurniture'] = settings.TableChairsAdvanceNotice;
            obj.warnings['includesTent'] = settings.TentAdvanceNotice;
            obj.warnings['includesTrash'] = settings.TrashCansAdvanceNotice;
        }

        if (!obj.title?.trim()?.length)
            obj.errors['title'] = "Title is required.";

        obj.additionalContactEmail = obj.additionalContact?.emailAddress ?? '';

        if (!obj.description?.trim()?.length)
            obj.errors['description'] = "Description is required.";

        if (!obj.location?.trim()?.length)
            obj.errors['location'] = "Location is required.";

        if (!obj.inBell && !obj.inCharlotte && !obj.inKoury && !obj.inKenan && !obj.inMcColl)
            obj.errors['building'] = 'At least one building must be selected.';

        const startDate = new Date(obj.startDate);
        const endDate = new Date(obj.endDate);

        if (obj.startDate == DateUtilityService.nullDateString)
            obj.errors['startDate'] = "Start date and time is required.";
        else {
            const startMinutes = startDate.getMinutes();

            if ((startMinutes != 0) && (startMinutes != 30))
                obj.errors['startDate'] = "Select a time ending in :00 or :30.";
        }

        if (obj.endDate == DateUtilityService.nullDateString)
            obj.errors['endDate'] = "End date and time is required.";
        else {
            const endMinutes = endDate.getMinutes();

            if ((endMinutes != 0) && (endMinutes != 30))
                obj.errors['endDate'] = "Select a time ending in :00 or :30.";
        }

        if ((obj.startDate != DateUtilityService.nullDateString) && (obj.endDate != DateUtilityService.nullDateString)) {
            if (startDate >= endDate)
                obj.errors['endDate'] = 'End date and time must be later than start date and time.';
        }


        if (!obj.includesHelp && !obj.includesCatering && !obj.includesTent && !obj.includesAv && !obj.includesHousekeeping && !obj.includesFurniture && !obj.includesAventri && !obj.includesTrash)
            obj.errors['requirements'] = 'At least one requirement must be selected.';

        if (obj.includesCatering) {
            if (!obj.cateringDescription?.trim()?.length)
                obj.errors['cateringDescription'] = 'Additional food details are required.';
        }
        else
            obj.cateringDescription = '';

        if (obj.includesTent) {
            if (!obj.tentDescription?.trim()?.length)
                obj.errors['tentDescription'] = 'Additional tent details are required.';
        }
        else
            obj.tentDescription = '';

        if (obj.includesFurniture) {
            if ((obj.tableCount < 1) && (obj.chairCount < 1))
                obj.errors['furniture'] = 'Either the number of tables or chairs must be entered.';

            if (obj.tableCount < 0)
                obj.errors['tableCount'] = 'Cannot be negative number.';

            if (obj.chairCount < 0)
                obj.errors['chairCount'] = 'Cannot be negative number.';
        }
        else {
            obj.tableCount = 0;
            obj.chairCount = 0;
        }

        if (obj.includesTrash) {
            if (obj.trashCanCount < 1)
                obj.errors['trashCanCount'] = `Number of trash cans is required.`;

            if (obj.trashCanCount < 0)
                obj.errors['trashCanCount'] = 'Cannot be negative number.';
        }
        else
            obj.trashCanCount = 0;
           
        obj.syncAlerts?.();

        if (Object.keys(obj.errors).length)
            obj.hasErrors = true;
    }
}
